import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React from 'react';
import matchMedia from 'matchmediaquery';
import hyphenate from 'hyphenate-style-name';
import areObjectsEqual from 'shallow-equal/objects';
import toQuery from './toQuery';
import Context from './Context';

var makeQuery = function makeQuery(settings) {
  return settings.query || toQuery(settings);
};

var hyphenateKeys = function hyphenateKeys(obj) {
  if (!obj) return null;
  var keys = Object.keys(obj);
  if (keys.length === 0) return null;
  return keys.reduce(function (result, key) {
    result[hyphenate(key)] = obj[key];
    return result;
  }, {});
};

var useIsUpdate = function useIsUpdate() {
  var ref = React.useRef(false);
  React.useEffect(function () {
    ref.current = true;
  }, []);
  return ref.current;
};

var useDevice = function useDevice(deviceFromProps) {
  var deviceFromContext = React.useContext(Context);

  var getDevice = function getDevice() {
    return hyphenateKeys(deviceFromProps) || hyphenateKeys(deviceFromContext);
  };

  var _React$useState = React.useState(getDevice),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      device = _React$useState2[0],
      setDevice = _React$useState2[1];

  React.useEffect(function () {
    var newDevice = getDevice();

    if (!areObjectsEqual(device, newDevice)) {
      setDevice(newDevice);
    }
  }, [deviceFromProps, deviceFromContext]);
  return device;
};

var useQuery = function useQuery(settings) {
  var getQuery = function getQuery() {
    return makeQuery(settings);
  };

  var _React$useState3 = React.useState(getQuery),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      query = _React$useState4[0],
      setQuery = _React$useState4[1];

  React.useEffect(function () {
    var newQuery = getQuery();

    if (query !== newQuery) {
      setQuery(newQuery);
    }
  }, [settings]);
  return query;
};

var useMatchMedia = function useMatchMedia(query, device) {
  var getMatchMedia = function getMatchMedia() {
    return matchMedia(query, device || {}, !!device);
  };

  var _React$useState5 = React.useState(getMatchMedia),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      mq = _React$useState6[0],
      setMq = _React$useState6[1];

  var isUpdate = useIsUpdate();
  React.useEffect(function () {
    if (isUpdate) {
      // skip on mounting, it has already been set
      setMq(getMatchMedia());
    }

    return function () {
      mq.dispose();
    };
  }, [query, device]);
  return mq;
};

var useMatches = function useMatches(mediaQuery) {
  var _React$useState7 = React.useState(mediaQuery.matches),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      matches = _React$useState8[0],
      setMatches = _React$useState8[1];

  React.useEffect(function () {
    var updateMatches = function updateMatches() {
      setMatches(mediaQuery.matches);
    };

    mediaQuery.addListener(updateMatches);
    updateMatches();
    return function () {
      mediaQuery.removeListener(updateMatches);
    };
  }, [mediaQuery]);
  return matches;
};

var useMediaQuery = function useMediaQuery(settings, device, onChange) {
  var deviceSettings = useDevice(device);
  var query = useQuery(settings);
  if (!query) throw new Error('Invalid or missing MediaQuery!');
  var mq = useMatchMedia(query, deviceSettings);
  var matches = useMatches(mq);
  var isUpdate = useIsUpdate();
  React.useEffect(function () {
    if (isUpdate && onChange) {
      onChange(matches);
    }
  }, [matches]);
  return matches;
};

export default useMediaQuery;